import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { normalizeName } from "../normalizeName";
import { mutator } from "./mutator";

// Marketing attribution captured from the signup redirect (see
// services/ui/lib/signupAttribution.ts). Forwarded to the HQ lead webhook by
// the server mutator; stripped from the audit-log payload via stripKeys.
export const MAX_ATTRIBUTION_VALUE_LENGTH = 2000;

export const marketingAttributionSchema = z.object({
  lastUtmUrl: z.string().max(MAX_ATTRIBUTION_VALUE_LENGTH).optional(),
  lastReferrer: z.string().max(MAX_ATTRIBUTION_VALUE_LENGTH).optional(),
  firstLandingPage: z.string().max(MAX_ATTRIBUTION_VALUE_LENGTH).optional(),
  gclid: z.string().max(MAX_ATTRIBUTION_VALUE_LENGTH).optional(),
  gaClientId: z.string().max(MAX_ATTRIBUTION_VALUE_LENGTH).optional(),
  gaSessionId: z.string().max(MAX_ATTRIBUTION_VALUE_LENGTH).optional(),
});

export type MarketingAttribution = z.infer<typeof marketingAttributionSchema>;

export const completeProfileSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  companyName: z.string().trim().min(1),
  department: z.string().trim().min(1),
  seniority: z.string().trim().min(1),
  marketingOptIn: z.boolean(),
  privacyAccepted: z.boolean(),
  termsAccepted: z.boolean(),
  marketingAttribution: marketingAttributionSchema.optional(),
});

export type CompleteProfileArgs = z.infer<typeof completeProfileSchema>;

export const completeProfile = mutator({
  stripKeys: {
    marketingAttribution: Object.keys(marketingAttributionSchema.shape),
  },
  schema: completeProfileSchema,
  resolve: {},
  permissions: {
    none: "completes the caller’s own profile; seeds the tenant company name when unset",
  },
  fn: async ({ tx, ctx, args }) => {
    if (!args.termsAccepted || !args.privacyAccepted) {
      throw userFacingError(
        "Please accept the terms and privacy policy to continue."
      );
    }
    const name = normalizeName(`${args.firstName} ${args.lastName}`);
    await tx.mutate.user.update({
      id: ctx.userId,
      name,
      hasCompletedPostSignupForm: true,
    });
    // First submitter in a tenant seeds its company name; later submissions
    // leave it in place so we can prefill their forms.
    const tenant = await tx.run(zql.tenants.where("id", ctx.tenantId).one());
    if (!tenant?.companyName) {
      await tx.mutate.tenants.update({
        id: ctx.tenantId,
        companyName: args.companyName,
        updatedAt: Date.now(),
      });
    }
  },
});
